import { createContext, ReactNode, useContext, useMemo } from 'react';

export type BrandType = 'enhanced-services' | 'aim-high';

type BrandConfig = {
  brand: BrandType;
  pageTitle: string;
  footerTitle: string;
  privacyPolicyName: string;
  taxId: string;
};

const BrandConfigContext = createContext<BrandConfig | undefined>(undefined);

const validateBrand = (brandEnv: string | undefined): BrandType => {
  const normalizedBrand = brandEnv === 'aim-high' ? 'aim-high' : 'enhanced-services';

  switch (normalizedBrand) {
    case 'aim-high':
      return 'aim-high';
    case 'enhanced-services':
      return 'enhanced-services';
    default:
      // eslint-disable-next-line no-case-declarations
      const _exhaustiveCheck: never = normalizedBrand;
      return _exhaustiveCheck;
  }
};

const getBrand = (): BrandConfig => {
  const brandEnv = import.meta.env.VITE_APP_BRAND || 'enhanced-services';
  const brand = validateBrand(brandEnv);

  switch (brand) {
    case 'aim-high':
      return {
        brand,
        pageTitle: "Aim High Children's Services",
        footerTitle: "Aim High Children's Services",
        privacyPolicyName: "Aim High Children's Services Privacy Policy",
        taxId: '11-3456448',
      };
    case 'enhanced-services':
      return {
        brand,
        pageTitle: 'Enhanced Services',
        footerTitle: 'Enhanced Support Services Inc.',
        privacyPolicyName: 'ESS Privacy Policy',
        taxId: '13-3456448',
      };
    default:
      // eslint-disable-next-line no-case-declarations
      const _exhaustiveCheck: never = brand;
      return _exhaustiveCheck;
  }
};

type BrandConfigProviderProps = {
  children: ReactNode;
};

export const BrandConfigProvider = ({ children }: BrandConfigProviderProps) => {
  const config = useMemo(() => getBrand(), []);

  return <BrandConfigContext.Provider value={config}>{children}</BrandConfigContext.Provider>;
};

export const useBrandConfig = (): BrandConfig => {
  const context = useContext(BrandConfigContext);
  if (!context) {
    throw new Error('useBrandConfig must be used within BrandConfigProvider');
  }
  return context;
};
